import type { PropertyPublic } from "@townsy/contracts";
import { STADIUM_SITES } from "@townsy/world";

/** Display names and ordering shared by the property panel and the directory (pure, unit-tested). */
export function propertyDisplayName(item: PropertyPublic | null | undefined): string {
  if (!item) return "Loading…";
  if (item.type === "CIVIC") return item.civic?.name ?? "Town building";
  if (item.type === "RESIDENTIAL") return item.status === "OWNED" ? `Home at ${item.address}` : "Plot for sale";
  return item.placement?.businessName ?? "Available building";
}

export const PROPERTY_TYPE_LABEL: Record<PropertyPublic["type"], string> = {
  COMMERCIAL: "Commercial",
  RESIDENTIAL: "Residential",
  CIVIC: "Town service",
};

/** Unowned residential plot that is sold at the Real Estate office. */
export function isPlotForSale(p: Pick<PropertyPublic, "type" | "status" | "ownershipIntervalId">): boolean {
  return p.type === "RESIDENTIAL" && p.status === "AVAILABLE" && !p.ownershipIntervalId;
}

/** Civic buildings (Townsy Real Estate, Home Builders, Furnishings) first, then the original manifest order. */
export function directoryOrder<T extends Pick<PropertyPublic, "type">>(items: readonly T[]): T[] {
  return items
    .map((p, i) => ({ p, i }))
    .sort((a, b) => (a.p.type === "CIVIC" ? 0 : 1) - (b.p.type === "CIVIC" ? 0 : 1) || a.i - b.i)
    .map((x) => x.p);
}

const CIVIC_MONOGRAMS: Record<string, string> = {
  real_estate_office: "RE",
  home_builders: "HB",
  furnishings: "F",
  casino: "TC",
  race_track: "TD",
  sports_center: "SC",
  truck_arena: "MT",
  // ADR-0044: the Sports Park's stadiums (their sign monograms, town/stadiums.ts colours).
  football_stadium: "TF",
  soccer_stadium: "FC",
  baseball_stadium: "SB",
  basketball_arena: "HA",
  hockey_arena: "BI",
};

/** ADR-0044: the Townsy home team of a Sports Park stadium ("Townsy Titans"), from the world's sites; null otherwise. */
export function homeTeamOf(civic: Pick<NonNullable<PropertyPublic["civic"]>, "kind"> | null | undefined): string | null {
  if (!civic) return null;
  return STADIUM_SITES.find((s) => s.kind === civic.kind)?.homeTeam ?? null;
}

/** "home of the Townsy Titans"; a club is named without the article ("home of Townsy FC"). */
export function homeOf(team: string): string {
  return `home of ${/\bFC$/.test(team) ? "" : "the "}${team}`;
}

/**
 * The full label of a place where it has one beyond its name (ADR-0044: "Titans Field — home of the Townsy Titans");
 * otherwise its display name.
 */
export function propertyFullName(item: PropertyPublic | null | undefined): string {
  const name = propertyDisplayName(item);
  const team = item?.type === "CIVIC" ? homeTeamOf(item.civic) : null;
  return team ? `${name} — ${homeOf(team)}` : name;
}

/** Monogram for buildings without a business logo (homes, Townsy-run shops). */
export function logoFallback(item: Pick<PropertyPublic, "type" | "civic"> | null | undefined): string {
  if (!item) return "·";
  if (item.type === "RESIDENTIAL") return "R";
  if (item.type === "CIVIC") return CIVIC_MONOGRAMS[item.civic?.kind ?? "real_estate_office"] ?? "RE";
  return "·";
}
